/**
 * SPL Uniformity heatmap — mobile's light-themed Canvas2D counterpart to
 * web's `web/src/components/room/charts/splUniformityDraw.ts`. Same
 * geometry (`computeSplUniformityGeometry`) and the same 20 dB
 * intensity-ramp convention (`intensityAlpha`), only the ink differs. A
 * masked-out (non-air) cell stays unpainted (transparent) rather than being
 * given a fabricated colour.
 */
import { canvasTypeFont } from '@roomtreatment/design';
import type { ChartPad } from '../layout';
import { computeSplUniformityGeometry } from '../splUniformity';
import { setupCanvas, drawEmptyMessage, drawCells, intensityAlpha, withAlpha, INK } from './_shared';

export const SPL_UNIFORMITY_PAD: ChartPad = { t: 10, r: 14, b: 22, l: 34 };

/** Matches web's splUniformityDraw.ts: 0 dB (loudest) = full ink, -20 dB or
 *  below = paper. Same scale convention as the Spectrogram chart, applied
 *  to a room-plan slice instead of a time/frequency grid. */
const SPL_RANGE_DB = 20;

export interface DrawSplUniformityOpts {
  nx: number;
  nz: number;
  values: number[];
  inside?: boolean[] | null;
  roomWidthM: number;
  roomDepthM: number;
  width: number;
  height: number;
}

export function drawSplUniformityCanvas(canvas: HTMLCanvasElement, opts: DrawSplUniformityOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeSplUniformityGeometry({
    nx: opts.nx,
    nz: opts.nz,
    values: opts.values,
    inside: opts.inside,
    roomWidthM: opts.roomWidthM,
    roomDepthM: opts.roomDepthM,
    width: opts.width,
    height: opts.height,
    pad: SPL_UNIFORMITY_PAD,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No volumetric field yet');
    return;
  }

  const cells = geo.cells.map((cell) => ({
    x0: cell.x0,
    y0: cell.y0,
    x1: cell.x1,
    y1: cell.y1,
    color: cell.levelDb == null ? null : withAlpha(INK.primary, intensityAlpha(cell.levelDb, SPL_RANGE_DB)),
  }));
  drawCells(ctx, geo.frame, cells);

  ctx.font = canvasTypeFont('tick');
  ctx.fillStyle = INK.label;
  ctx.textAlign = 'center';
  for (const t of geo.xTicks) {
    ctx.fillText(t.label, geo.frame.plotX + t.x, geo.frame.plotY + geo.frame.plotH + 12);
  }
  ctx.textAlign = 'right';
  for (const t of geo.zTicks) {
    ctx.fillText(t.label, geo.frame.plotX - 4, geo.frame.plotY + t.y + 3);
  }
}
